import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { PropagateLoader } from "react-spinners";
import config from "../../Config";

export default function ProductReviews({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [productReview, setProductReview] = useState({
    totalRating: 0,
    averageRating: 0,
    totalStar: 0,
    starCounts: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${config.apiBaseUrl}/{config.getReview}`,
          {
            method: "GET",
            headers: {
              "product-id": productId,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch reviews");
        }

        const data = await response.json();

        setReviews(Array.isArray(data?.reviewData) ? data.reviewData : []);

        setProductReview({
          totalRating: Number(data?.productReview?.totalRating || 0),
          averageRating: Number(data?.productReview?.averageRating || 0),
          totalStar: Number(data?.productReview?.totalStar || 0),
          starCounts: {
            1: Number(data?.productReview?.starCounts?.[1] || 0),
            2: Number(data?.productReview?.starCounts?.[2] || 0),
            3: Number(data?.productReview?.starCounts?.[3] || 0),
            4: Number(data?.productReview?.starCounts?.[4] || 0),
            5: Number(data?.productReview?.starCounts?.[5] || 0),
          },
        });
      } catch (error) {
        setReviews([]);
        setProductReview({
          totalRating: 0,
          averageRating: 0,
          totalStar: 0,
          starCounts: {
            1: 0,
            2: 0,
            3: 0,
            4: 0,
            5: 0,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchReviews();
    } else {
      setLoading(false);
    }
  }, [productId]);

  const totalReviews = Number(productReview?.totalRating || 0);

  const averageRating = Number(productReview?.averageRating || 0);

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        px: {
          xs: 2,
          sm: 3,
          md: 0,
        },
      }}
    >
      <Box
        sx={{
          mb: {
            xs: 4,
            md: 5,
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 1,
              background: "#977743",
            }}
          />

          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "#977743",
            }}
          >
            Client Experience
          </Typography>
        </Stack>

        <Typography
          component="h2"
          sx={{
            fontFamily: "Georgia, serif",
            fontWeight: 400,
            fontSize: {
              xs: 34,
              md: 46,
            },
            lineHeight: 1.1,
            letterSpacing: "-0.035em",
          }}
        >
          Product Reviews
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          background: "#fff",
          border: "1px solid #ddd7ce",
          borderRadius: 0,
          overflow: "hidden",
          mb: {
            xs: 5,
            md: 7,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "0.8fr 1.6fr",
            },
          }}
        >
          <Box
            sx={{
              p: {
                xs: 3,
                md: 5,
              },
              display: "flex",
              flexDirection: "column",
              alignItems: {
                xs: "center",
                md: "flex-start",
              },
              justifyContent: "center",
              background: "#f1ede7",
              borderRight: {
                md: "1px solid #ddd7ce",
              },
              borderBottom: {
                xs: "1px solid #ddd7ce",
                md: "none",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#77716a",
                mb: 2,
              }}
            >
              Overall Rating
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: {
                  xs: 58,
                  md: 66,
                },
                lineHeight: 1,
                fontWeight: 400,
              }}
            >
              {averageRating.toFixed(1)}
            </Typography>

            <Rating
              value={averageRating}
              precision={0.1}
              readOnly
              icon={
                <StarRoundedIcon
                  sx={{
                    color: "#977743",
                  }}
                  fontSize="inherit"
                />
              }
              emptyIcon={
                <StarRoundedIcon
                  sx={{
                    color: "#d8d1c7",
                  }}
                  fontSize="inherit"
                />
              }
              sx={{
                mt: 1.5,
                fontSize: 22,
              }}
            />

            <Typography
              sx={{
                mt: 1,
                fontSize: 11,
                color: "#77716a",
                letterSpacing: "0.04em",
              }}
            >
              {totalReviews} {totalReviews === 1 ? "review" : "reviews"}
            </Typography>
          </Box>

          <Box
            sx={{
              p: {
                xs: 3,
                md: 5,
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#171717",
                mb: 3,
              }}
            >
              Rating Distribution
            </Typography>

            <Stack spacing={1.8}>
              {[5, 4, 3, 2, 1].map((star) => {
                const count = Number(productReview?.starCounts?.[star] || 0);

                const percentage =
                  totalReviews > 0 ? (count / totalReviews) * 100 : 0;

                return (
                  <Box
                    key={star}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "32px 24px 1fr 35px",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#4f4a44",
                      }}
                    >
                      {star}
                    </Typography>

                    <StarRoundedIcon
                      sx={{
                        fontSize: 17,
                        color: "#977743",
                      }}
                    />

                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 5,
                        borderRadius: 0,
                        backgroundColor: "#ebe6df",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#171717",
                        },
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 11,
                        textAlign: "right",
                        color: "#77716a",
                      }}
                    >
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </Paper>

      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#977743",
                mb: 0.8,
              }}
            >
              What They Say
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: {
                  xs: 27,
                  md: 34,
                },
                fontWeight: 400,
              }}
            >
              Customer Stories
            </Typography>
          </Box>

          <Box
            sx={{
              display: {
                xs: "none",
                sm: "flex",
              },
              alignItems: "center",
              gap: 1,
              color: "#77716a",
            }}
          >
            <RateReviewOutlinedIcon sx={{ fontSize: 19 }} />

            <Typography
              sx={{
                fontSize: 11,
              }}
            >
              {totalReviews} reviews
            </Typography>
          </Box>
        </Stack>

        {loading ? (
          <Box
            sx={{
              minHeight: 180,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <PropagateLoader color="#977743" size={8} />
          </Box>
        ) : reviews.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #ddd7ce",
              borderRadius: 0,
              background: "#fff",
              py: {
                xs: 5,
                md: 7,
              },
              px: 3,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: "#f1ede7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <RateReviewOutlinedIcon
                sx={{
                  color: "#977743",
                }}
              />
            </Box>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: 26,
                mb: 1,
              }}
            >
              No reviews yet
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                color: "#77716a",
                lineHeight: 1.7,
                maxWidth: 420,
                mx: "auto",
              }}
            >
              Be the first to share your experience with this piece.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={0}>
            {reviews.map((review, index) => {
              const rating = Number(review?.rateStar || 0);

              const userData = review?.userData || {};

              const userName = userData?.name || "Hindustan Wardrobe Customer";

              const userAvatar = userData?.url || userData?.avatar || "";

              const reviewDate = review?.createdAt
                ? new Date(review.createdAt).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "";

              return (
                <React.Fragment key={review?.reviewId || review?._id || index}>
                  <Paper
                    elevation={0}
                    sx={{
                      background: "transparent",
                      border: 0,
                      borderRadius: 0,
                      py: {
                        xs: 3,
                        md: 4,
                      },
                    }}
                  >
                    <Stack
                      direction={{
                        xs: "column",
                        sm: "row",
                      }}
                      spacing={{
                        xs: 2,
                        sm: 3,
                      }}
                    >
                      <Avatar
                        src={userAvatar}
                        sx={{
                          width: 48,
                          height: 48,
                          background: "#e9e4dc",
                          color: "#977743",
                          flexShrink: 0,
                        }}
                      >
                        {!userAvatar && <PersonOutlineRoundedIcon />}
                      </Avatar>

                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          justifyContent="space-between"
                          alignItems={{
                            xs: "flex-start",
                            sm: "center",
                          }}
                          spacing={1}
                          sx={{ mb: 1.2 }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: "#171717",
                              }}
                            >
                              {userName}
                            </Typography>

                            {reviewDate && (
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: "#8b857d",
                                  mt: 0.4,
                                }}
                              >
                                {reviewDate}
                              </Typography>
                            )}
                          </Box>

                          <Rating
                            value={rating}
                            readOnly
                            size="small"
                            icon={
                              <StarRoundedIcon
                                sx={{
                                  color: "#977743",
                                }}
                                fontSize="inherit"
                              />
                            }
                            emptyIcon={
                              <StarRoundedIcon
                                sx={{
                                  color: "#ddd7ce",
                                }}
                                fontSize="inherit"
                              />
                            }
                          />
                        </Stack>

                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#514c46",
                            lineHeight: 1.85,
                            maxWidth: 820,
                          }}
                        >
                          {review?.comment ||
                            "This customer did not leave a written comment."}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {index < reviews.length - 1 && (
                    <Divider
                      sx={{
                        borderColor: "#ddd7ce",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Stack>
        )}
      </Box>
    </Container>
  );
}
